import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SortOption } from '../../types/lists';

interface Props {
  placeholder?: string;
  search: string;
  onSearchChange: (v: string) => void;
  sortValue?: string;
  onSortChange?: (v: string) => void;
  sortOptions?: SortOption[];
  filtersLabel?: string;
  extra?: ReactNode;
  className?: string;
}

export function ListToolbar({
  placeholder,
  search,
  onSearchChange,
  sortValue,
  onSortChange,
  sortOptions,
  filtersLabel,
  extra,
  className = '',
}: Props) {
  const { t } = useTranslation('common');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortOpen) return;
    function handle(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [sortOpen]);

  const activeSort = sortOptions?.find((o) => o.id === sortValue);
  const searchPlaceholder = placeholder ?? t('actions.search', { defaultValue: 'Suchen…' });

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <label className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12.5px] flex-1 min-w-[180px] sm:flex-initial sm:w-[260px]">
        <Search className="w-3.5 h-3.5 text-slate-400" strokeWidth={2} aria-hidden />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="bg-transparent border-0 outline-none flex-1 text-slate-900 placeholder:text-slate-400"
        />
      </label>

      {filtersLabel && (
        <span className="hidden sm:inline-flex px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] text-slate-500">
          {filtersLabel}
        </span>
      )}

      {sortOptions && sortOptions.length > 0 && onSortChange && (
        <div ref={sortRef} className="relative ml-auto">
          <button
            type="button"
            onClick={() => setSortOpen((v) => !v)}
            className="px-2.5 py-1.5 rounded-md bg-white border border-slate-200 text-[12px] text-slate-700 flex items-center gap-1.5 hover:bg-slate-50 transition-colors"
          >
            <span className="text-slate-400">{t('actions.sort', { defaultValue: 'Sortieren:' })}</span>
            <span className="font-medium">{activeSort?.label ?? '—'}</span>
            <ChevronDown className="w-3 h-3" strokeWidth={2} aria-hidden />
          </button>
          {sortOpen && (
            <div
              className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-md border border-slate-200 bg-white py-1"
              style={{ boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)' }}
            >
              {sortOptions.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onSortChange(opt.id);
                    setSortOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1.5 text-[12.5px] transition-colors ${
                    opt.id === sortValue
                      ? 'bg-emerald-50 text-[#00A871] font-medium'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {extra}
    </div>
  );
}
